import { motion } from 'framer-motion';
import {
  Heart,
  Shield,
  Users,
  CheckCircle,
  ArrowRight,
  Target,
  Compass,
  AlertCircle,
  TrendingUp,
  Award,
  Flag,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHero from '../../components/PageHero';
import { getPageHeroImageUrl } from '../../config/cloudflareAssets';

export default function CoreValuesPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const valueCategories = [
    {
      name: 'Integrity',
      icon: Shield,
      description: 'Ethical foundation and trustworthiness',
      examples: [
        'Honesty in all dealings',
        'Transparency with stakeholders',
        'Accountability for actions',
        'Ethical decision-making',
        'Keeping commitments',
      ],
      when: 'Critical for regulated industries, B2B trust, and long-term partnerships',
    },
    {
      name: 'Excellence',
      icon: Award,
      description: 'Commitment to quality and continuous improvement',
      examples: [
        'Quality craftsmanship',
        'Innovation and creativity',
        'Continuous improvement mindset',
        'Attention to detail',
        'Professional mastery',
      ],
      when: 'Essential when competing on quality, expertise, or premium positioning',
    },
    {
      name: 'People & Culture',
      icon: Users,
      description: 'How you treat employees and build teams',
      examples: [
        'Respect and dignity',
        'Diversity and inclusion',
        'Collaboration over competition',
        'Work-life balance',
        'Employee development',
      ],
      when: 'Key for talent-intensive businesses and employee-centric cultures',
    },
    {
      name: 'Customer Centricity',
      icon: Heart,
      description: 'Obsessive focus on customer success',
      examples: [
        'Customer-first decisions',
        'Service excellence',
        'Long-term partnerships',
        'Value creation',
        'Listening and responding',
      ],
      when: 'Vital for service businesses, SaaS, and relationship-based models',
    },
    {
      name: 'Impact & Purpose',
      icon: Target,
      description: 'Making a positive difference beyond profit',
      examples: [
        'Social responsibility',
        'Environmental sustainability',
        'Community engagement',
        'Purpose-driven',
        'Leave it better',
      ],
      when: 'Increasingly important for mission-driven organizations and conscious consumers',
    },
    {
      name: 'Performance & Results',
      icon: TrendingUp,
      description: 'Achievement orientation and operational excellence',
      examples: [
        'Results-oriented',
        'Efficiency and speed',
        'Agility and adaptability',
        'Data-driven decisions',
        'Ownership mindset',
      ],
      when: 'Critical for high-growth companies and performance-driven cultures',
    },
  ];

  const companyExamples = [
    {
      company: 'Netflix',
      values: [
        'Judgment',
        'Communication',
        'Courage',
        'Passion',
        'Selflessness',
        'Innovation',
        'Inclusion',
        'Integrity',
        'Impact',
      ],
      insight:
        "Netflix's famous culture deck details not just values but expected behaviors for each. They hire and fire based on cultural fit.",
      implementation:
        'Annual "keeper test": Would you fight to keep this person? If not, give them a generous severance. Controversial but creates high-performance culture.',
    },
    {
      company: 'Patagonia',
      values: [
        'Build the best product',
        'Cause no unnecessary harm',
        'Use business to protect nature',
        'Not bound by convention',
      ],
      insight:
        'Values directly drive radical business decisions like donating company to environmental trust, repairing products instead of selling new ones.',
      implementation:
        'Values inform every decision from supply chain to marketing. "Is it good for the planet?" is a decision filter.',
    },
    {
      company: 'Zappos',
      values: [
        'Deliver WOW Through Service',
        'Embrace and Drive Change',
        'Create Fun and A Little Weirdness',
        'Be Adventurous, Creative, and Open-Minded',
        'Be Humble',
      ],
      insight:
        'Built entire business model around values. Offers new hires $2,000 to quit after training—ensures only values-aligned people stay.',
      implementation:
        "Values are hiring criteria, performance metrics, and firing reasons. Not aspirational—they're required.",
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Foundation', path: '/foundation' },
              { label: 'Core Values' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Compass className="w-4 h-4 mr-2" />
            Foundation Step 3 of 4
          </span>
        }
        title="Values that guide the difficult decisions"
        subtitle="Move beyond words on a wall. Define the principles and everyday behaviors that shape how your company hires, works, and earns trust."
      >
        <Link
          to="/sign-up"
          className="public-primary-button inline-flex items-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90"
        >
          Define your values
        </Link>
      </PageHero>

      {/* Why Values Matter */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Make your principles useful under pressure
              </h2>
              <p className="text-xl text-gray-600">
                Values matter most when the easy choice and the right choice differ. Define the
                behaviors your team should expect, especially when trade-offs get difficult.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: Users,
                  title: 'Guide Decision-Making',
                  description:
                    'When faced with tough choices, values provide a framework. "Does this align with our values?" becomes a practical decision filter at all levels.',
                  impact: 'Speeds decisions and ensures consistency across organization',
                },
                {
                  icon: Target,
                  title: 'Attract Right Talent',
                  description:
                    'People join companies whose values align with their own. Clear values attract cultural fit and repel poor matches before they join.',
                  impact: 'Reduces hiring mistakes and improves retention',
                },
                {
                  icon: Shield,
                  title: 'Build Trust & Reputation',
                  description:
                    'Consistently living your values builds trust with customers, partners, and communities. Trust is your most valuable intangible asset.',
                  impact: 'Stronger brand, customer loyalty, premium pricing power',
                },
                {
                  icon: TrendingUp,
                  title: 'Drive Performance',
                  description:
                    'Shared values create cohesion. When everyone understands and commits to core principles, coordination improves and execution accelerates.',
                  impact: 'Higher productivity, less internal friction, faster execution',
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <item.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-xs text-gray-700">
                      <span className="font-semibold text-green-700">Impact:</span> {item.impact}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value Categories */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Common Value Categories</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore these categories to identify what matters most to your organization
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {valueCategories.map((cat, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="public-card bg-white p-6 rounded-xl border border-gray-200"
              >
                <div className="bg-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <cat.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-blue-600">{cat.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{cat.description}</p>
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-900 mb-2">Examples:</p>
                  <ul className="space-y-1">
                    {cat.examples.map((ex, j) => (
                      <li key={j} className="text-xs text-gray-600 flex items-start">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1.5 mt-0.5 flex-shrink-0" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-blue-50 p-2 rounded text-xs text-gray-700">
                  <span className="font-semibold">Use When:</span> {cat.when}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Define Values */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">How to Define Your Core Values</h2>
              <p className="text-xl text-gray-600">
                A systematic process for discovering and articulating your values
              </p>
            </motion.div>
            <div className="space-y-6">
              {[
                {
                  step: '1',
                  title: 'Discover Existing Values',
                  description:
                    "Your values already exist—they're revealed in how you actually make decisions, not what you wish guided decisions. Look at critical past choices: What principles actually guided you?",
                  tip: 'Ask: "When we faced tough trade-offs, what did we prioritize? What would we never compromise on even under pressure?"',
                },
                {
                  step: '2',
                  title: 'Gather Diverse Input',
                  description:
                    "Don't let founders or executives define values alone. Survey employees at all levels, long-tenured and new. What do they believe the company stands for?",
                  tip: 'Look for patterns in the feedback. Values that appear organically across responses are real. Values only leadership mentions are aspirational at best.',
                },
                {
                  step: '3',
                  title: 'Prioritize Ruthlessly',
                  description:
                    "Everything can't be a core value. Limit to 3-7 principles that are truly non-negotiable. More than that and they lose meaning and memorability.",
                  tip: 'Test each candidate value: Would we fire someone for violating this? If not, it\'s not really "core." ',
                },
                {
                  step: '4',
                  title: 'Define Behaviors, Not Just Words',
                  description:
                    'Don\'t stop at naming values. Define what each looks like in practice with specific, observable behaviors. "Integrity" is vague. "We admit mistakes quickly and make them right" is actionable.',
                  tip: 'For each value, write: "This value means we [specific behavior]. It doesn\'t mean we [common misconception]."',
                },
                {
                  step: '5',
                  title: 'Integrate Into Systems',
                  description:
                    'Embed values into hiring (values-based interview questions), performance reviews (values as evaluation criteria), recognition (celebrate values exemplars), and decisions (values as filters).',
                  tip: "If values aren't in your performance review template or hiring scorecard, they're just words on a wall.",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-blue-700">Pro Tip:</span> {item.tip}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Real Examples */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Company Examples: Values in Action</h2>
            <p className="text-xl text-gray-600">
              Organizations that actually live their values—and how they do it
            </p>
          </motion.div>
          <div className="space-y-8 max-w-5xl mx-auto">
            {companyExamples.map((ex, i) => (
              <motion.div
                key={i}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="public-card bg-white p-8 rounded-xl border border-gray-200"
              >
                <h3 className="text-2xl font-bold mb-4">{ex.company}</h3>
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Core Values:</p>
                  <div className="flex flex-wrap gap-2">
                    {ex.values.map((v, j) => (
                      <span
                        key={j}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {v}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg mb-3">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Why It Works:</span> {ex.insight}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-green-700">Implementation:</span>{' '}
                    {ex.implementation}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Common Core Values Mistakes</h2>
              <p className="text-xl text-gray-600">
                Avoid these traps that make values meaningless
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  mistake: 'Aspirational Not Actual',
                  description:
                    'Listing values you wish you had rather than principles you actually live by. "Innovation" isn\'t a value if you punish failure and resist change.',
                  fix: "Be honest. Identify values that already guide your best decisions and behaviors. Then work to strengthen them—don't fake values you don't have.",
                },
                {
                  mistake: 'Too Many Values',
                  description:
                    'Listing 15+ values because "they\'re all important." Result: nothing is actually core. People can\'t remember them, let alone use them.',
                  fix: "Limit to 3-7 core values maximum. If you can't prioritize, you don't truly know what matters most. Brutal prioritization clarifies culture.",
                },
                {
                  mistake: 'Values Without Behaviors',
                  description:
                    'Stating "Integrity" without defining what integrity means in your context. Abstract values don\'t guide action—behavioral definitions do.',
                  fix: 'For each value, provide 3-5 specific observable behaviors. "Integrity means: we admit mistakes immediately, we keep commitments, we don\'t blame others."',
                },
                {
                  mistake: 'No Consequences for Violations',
                  description:
                    'Values posted everywhere but violations are ignored or minimized. People quickly learn values are just PR, not real standards.',
                  fix: "Publicly recognize values exemplars. Clearly address violations, even (especially) when it's uncomfortable or costly. Actions > words.",
                },
                {
                  mistake: "Leadership Doesn't Model Values",
                  description:
                    'Executives saying "customer first" while treating customers poorly. Or preaching "work-life balance" while emailing at midnight. Hypocrisy destroys credibility.',
                  fix: 'Leaders must model values visibly and consistently. When leaders violate values, address it immediately—no exceptions for performance.',
                },
                {
                  mistake: 'Generic Values Everyone Claims',
                  description:
                    '"Quality, Integrity, Teamwork, Innovation"—so generic they could apply to any company. Don\'t differentiate or guide unique decisions.',
                  fix: "Make at least 1-2 values distinctive to your culture. What's non-negotiable for you that competitors might compromise on?",
                },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="public-card bg-white p-6 rounded-xl border border-red-200"
                >
                  <div className="flex items-start space-x-3 mb-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    <h3 className="text-lg font-bold text-gray-900">{item.mistake}</h3>
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{item.description}</p>
                  <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-green-700">✓ Fix:</span> {item.fix}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Put the framework to work */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-blue-700 mb-3">
                YOUR NEXT STEP IN STRATPLAN
              </p>
              <h2 className="text-3xl font-bold mb-5">
                Turn beliefs into a clear standard for behavior
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Bring this framework into your company’s plan. Work through the questions, record
                your decisions, and connect them to the strategy you are building.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">01</p>
                <h3 className="text-xl font-bold mb-3">Find what already matters</h3>
                <p className="text-gray-600">
                  Look at past decisions to identify the principles your team actually lives by.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">02</p>
                <h3 className="text-xl font-bold mb-3">Define the behavior</h3>
                <p className="text-gray-600">
                  Explain what each value looks like in practice, with examples people can
                  recognize.
                </p>
              </div>
              <div className="public-card bg-white p-6 rounded-xl border border-gray-200">
                <p className="text-sm font-semibold text-blue-700 mb-3">03</p>
                <h3 className="text-xl font-bold mb-3">Put it to work</h3>
                <p className="text-gray-600">
                  Bring those expectations into hiring, feedback, recognition, and strategic
                  choices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Related Foundation Elements</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/foundation/mission-statement"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Flag className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-blue-600">Mission Statement</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Define your organization's fundamental purpose and reason for being
                </p>
                <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Target className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-purple-600">Strategic Objectives</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Set measurable goals that bring your mission and values to life
                </p>
                <div className="flex items-center text-purple-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
              <Link
                to="/execution-monitoring/accountability-ownership"
                className="public-card bg-white p-6 rounded-xl border border-gray-200 hover:border-gray-200 transition-all group"
              >
                <Shield className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="text-xl font-bold mb-2 text-indigo-600">Accountability Systems</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Build systems that ensure values are lived, not just stated
                </p>
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/foundation/mission-statement"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50"
              >
                ← Previous: Mission Statement
              </Link>
              <Link
                to="/foundation/strategic-objectives"
                className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                Next: Strategic Objectives <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Start your plan */}
      <section className="public-section public-cta py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-5">Define your values in StratPlan</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Bring those expectations into hiring, feedback, recognition, and strategic choices.
            Start with your company’s context and build a plan you can put to work.
          </p>
          <Link
            to="/sign-up"
            className="public-primary-button inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700"
          >
            Define your values <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
